import { useEffect, useState, useRef, createContext, useContext, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { invalidarUnidadesCache } from "./hooks/useBusinessUnits";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// SEC-004: CSRF double-submit — leer la cookie csrf_token e inyectarla como header
// en cualquier request que muta datos.
const _readCookie = (name) => {
  const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
};
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = _readCookie('csrf_token');
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// Global axios response interceptor — handle 401 and PENDING_APPROVAL redirects
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // SEC-003: si el backend responde 403 con code=PENDING_APPROVAL, redirigir
    if (error.response?.status === 403 && error.response?.data?.code === "PENDING_APPROVAL") {
      if (!window.location.pathname.startsWith("/esperando-aprobacion")) {
        window.location.href = "/esperando-aprobacion";
      }
    }
    // Only redirect to login if it's a 401 on an admin endpoint AND not the /auth/me check
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      // Don't redirect for /auth/me (initial auth check) or public endpoints
      if (!url.includes("/auth/me") && !url.includes("/catalogo/publico") && !url.includes("/firma/")) {
        // Session expired - silently handled by auth context
      }
    }
    return Promise.reject(error);
  }
);

// ============== CONTEXT ==============
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();

    // Poll /auth/me every 30s to detect status changes (e.g., admin approval)
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(prev => {
          if (!prev) return prev;
          // Only update if status or plan changed
          if (prev.suscripcion_status !== response.data.suscripcion_status ||
              prev.plan_id !== response.data.plan_id ||
              prev.role !== response.data.role) {
            return response.data;
          }
          return prev;
        });
      } catch (err) { console.error("Session poll failed:", err.message); }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/panel';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginEmail = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.success && data.user) {
      setUser(data.user);
    }
    return data;
  };

  const registerEmail = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    if (data.success && data.user) {
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      // Iter132 · Seguridad · limpiar cache de unidades de negocio para que la
      // siguiente sesión no herede unidades/permisos del usuario anterior.
      invalidarUnidadesCache();
      setUser(null);
    } catch {
      invalidarUnidadesCache();
      setUser(null);
    }
  };

  const contextValue = useMemo(() => ({ 
    user, setUser, loading, login, loginEmail, registerEmail, logout 
  }), [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== AUTH CALLBACK ==============
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      axios.post(`${API}/auth/session`, { session_id: sessionId })
        .then(response => {
          if (response.data.success && response.data.user) {
            setUser(response.data.user);
            const u = response.data.user;
            const isPending = u.role !== 'superadmin' && 
              (!u.suscripcion_status || u.suscripcion_status === 'pending' || u.suscripcion_status === 'under_review');
            if (isPending) {
              toast.success('Bienvenido. Tu plan esta pendiente de aprobacion.');
              navigate('/panel/mi-suscripcion', { replace: true });
            } else {
              navigate('/panel', { replace: true });
            }
          } else {
            toast.error('Error al iniciar sesión');
            navigate('/', { replace: true });
          }
        })
        .catch(() => {
          toast.error('Error de autenticación');
          navigate('/', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone-600">Iniciando sesión...</p>
      </div>
    </div>
  );
};

// ============== PROTECTED ROUTE ==============
// Wrapper que provee el WorkspaceContext con el user actual
const PanelWithWorkspace = () => {
  const { user } = useAuth();
  return (
    <ThemeProvider user={user}>
      <WorkspaceProvider user={user}>
        <AdminLayout />
      </WorkspaceProvider>
    </ThemeProvider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // No user → redirect to home
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Pending/under_review/null users can ONLY see Mi Suscripcion
  const status = user.suscripcion_status;
  const role = user.role;
  const isRestricted = role !== 'superadmin' && (!status || status === 'pending' || status === 'under_review' || status === 'expired');
  if (isRestricted && location.pathname !== '/panel/mi-suscripcion') {
    return <Navigate to="/panel/mi-suscripcion" replace />;
  }

  // Suscripciones route is superadmin-only
  if (location.pathname === '/panel/suscripciones' && role !== 'superadmin') {
    return <Navigate to="/panel" replace />;
  }

  // Iter131 · P1 · Mi empresa (/panel/configuracion) accesible a superadmin
  // Y a workspace_rol === "admin" del tenant (dueño/admin de empresa).
  // Antes solo superadmin, lo cual bloqueaba a los admin de tenants nuevos.
  const wsRolForConfig = (user.workspace_rol || "").toLowerCase();
  if (location.pathname === '/panel/configuracion' && role !== 'superadmin' && wsRolForConfig !== 'admin') {
    return <Navigate to="/panel" replace />;
  }

  // Unidades internas (Celebra Petit / La Kermesita / Bocatto) solo para Celebra Group
  const unidadesExclusivas = ['/panel/petit', '/panel/kermesita', '/panel/bocatto', '/panel/dashboard'];
  if (!user.es_celebra_group && unidadesExclusivas.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return <Navigate to="/panel/hoy" replace />;
  }

  // Iter124 · P0 · Roles solo-lectura-asignada (mesero/chef/cliente) bloqueados de rutas
  // administrativas por acceso directo por URL. Solo pueden ver: inicio, eventos, agenda,
  // mi-suscripcion. Chef mantiene acceso a producción/recetas/inventario que su rol permite.
  const wsRol = (user.workspace_rol || "").toLowerCase();
  if (["mesero", "chef", "cliente"].includes(wsRol) && role !== "superadmin") {
    const rutasBloqueadas = [
      '/panel/cotizar', '/panel/pipeline', '/panel/contratos', '/panel/clientes',
      '/panel/pagos', '/panel/whatsapp', '/panel/cotizador-config', '/panel/branding',
      '/panel/configuracion', '/panel/admin', '/panel/finanzas', '/panel/finanzas-pro',
      '/panel/nuevo-evento',
    ];
    if (rutasBloqueadas.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
      return <Navigate to="/panel/mi-panel" replace />;
    }
  }

  return children;
};

// ============== LAZY IMPORTS ==============
import LandingPage from "./pages/LandingPage";
import ActivarCuenta from "./pages/ActivarCuenta";
import LoginPage from "./pages/LoginPage";
import CotizadorPublico from "./pages/CotizadorPublico";
import ReservarPetit from "./pages/ReservarPetit";
import ClienteFormulario from "./pages/ClienteFormulario";
import EsperandoAprobacion from "./pages/EsperandoAprobacion";
import AdminLayout from "./layouts/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import DashboardConsolidado from "./pages/admin/DashboardConsolidado";
import Clientes from "./pages/admin/Clientes";
import ClienteDetalle from "./pages/admin/ClienteDetalle";
import Pagos from "./pages/admin/Pagos";
import Invitaciones from "./pages/admin/Invitaciones";
import CRMWhatsApp from "./pages/admin/CRMWhatsApp";
import Calendario from "./pages/admin/Calendario";
import Cotizaciones from "./pages/admin/Cotizaciones";
import Contratos from "./pages/admin/Contratos";
import Personal from "./pages/admin/Personal";
import Proveedores from "./pages/admin/Proveedores";
import Eventos from "./pages/admin/Eventos";
import MiSuscripcion from "./pages/admin/MiSuscripcion";
import Configuracion from "./pages/admin/Configuracion";
import InvitacionDesigner from "./pages/admin/InvitacionDesigner";
import Catalogo from "./pages/admin/Catalogo";
import CatalogoPublico from "./pages/CatalogoPublico";
import FirmaContrato from "./pages/FirmaContrato";
import Solicitudes from "./pages/admin/Solicitudes";
import ServiciosRapidos from "./pages/admin/ServiciosRapidos";
import Suscripciones from "./pages/admin/Suscripciones";
import Seguimientos from "./pages/admin/Seguimientos";
import HojaOperativa from "./pages/admin/HojaOperativa";
import CotizadorConfig from "./pages/admin/CotizadorConfig";
import CotizacionPublica from "./pages/CotizacionPublica";
import Finanzas from "./pages/admin/Finanzas";
import Inventario from "./pages/admin/Inventario";
import Compras from "./pages/admin/Compras";
import PlannerHoy from "./pages/admin/PlannerHoy";
import WizardNuevoEvento from "./pages/admin/WizardNuevoEvento";
import UnidadesPublicas from "./pages/admin/UnidadesPublicas";
import RolesInternos from "./pages/admin/RolesInternos";
import ReportesUnidades from "./pages/admin/ReportesUnidades";
import Recetas from "./pages/admin/Recetas";
import MenusCRUD from "./pages/admin/MenusCRUD";
import ExperienciasCRUD from "./pages/admin/ExperienciasCRUD";
// Iter124m · LEGACY: /pages/admin/CotizadorPublico.jsx queda como archivo sin uso runtime.
// import CotizadorPublicoAdmin from "./pages/admin/CotizadorPublico"; // ← intencionalmente deshabilitado.
import RecordatoriosWhatsApp from "./pages/admin/RecordatoriosWhatsApp";
import WhatsappPlantillas from "./pages/admin/WhatsappPlantillas";
import Auditoria from "./pages/admin/Auditoria";
import Plantillas from "./pages/admin/Plantillas";
import PlantillaBuilder from "./pages/admin/PlantillaBuilder";
import CotizarRapido from "./pages/admin/CotizarRapido";
import Produccion from "./pages/admin/Produccion";
import OperacionDashboard from "./pages/admin/OperacionDashboard";
import CotizarPublico from "./pages/CotizarPublico";
import AgendaEventos from "./pages/admin/AgendaEventos";
import ExpedienteEvento from "./pages/admin/ExpedienteEvento";
import DocumentosEvento from "./pages/admin/DocumentosEvento";
import ModoEvento from "./pages/admin/ModoEvento";
import MiAgenda from "./pages/admin/MiAgenda";
import MiAgendaPersonal from "./pages/admin/MiAgendaPersonal";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Branding from "./pages/admin/Branding";
import Pipeline from "./pages/admin/Pipeline";
import FinanzasNueva from "./pages/admin/FinanzasNueva";
import AdminEmpresas from "./pages/admin/AdminEmpresas";
import AdminAuditoria from "./pages/admin/AdminAuditoria";
import SinClasificar from "./pages/admin/SinClasificar";
import AdminUsuariosTenant from "./pages/admin/AdminUsuariosTenant";
import AdminUnidadesConfig from "./pages/admin/AdminUnidadesConfig";
import PanelRevisionManual from "./pages/creador/PanelRevisionManual";
import { DashboardGeneral, SeleccionUnidad } from "./pages/admin/DashboardsUnidades";
import { PanelPetit, PanelKermesita, PanelBocatto } from "./pages/admin/panel-unidad/PanelUnidad";
import ContratarUnidad from "./pages/ContratarUnidad";

// ============== APP ROUTER ==============

// Redirige rutas legacy del cotizador viejo al cotizador nuevo (vitrina). Iter93k
// Iter93l: si no hay usuarioId, redirigir al owner de la plataforma en lugar de landing
// (evita loop infinito cuando el botón "Cotizar Evento" apunta a /cotizador sin id).
const OWNER_USUARIO_ID = "user_d7abce64f0cf"; // La Kermesita / Celebra Group

function RedirectCotizador() {
  const { usuarioId } = useParams();
  const location = useLocation();
  const uid = usuarioId || new URLSearchParams(location.search).get("u") || OWNER_USUARIO_ID;
  return <Navigate to={`/cotizar/${uid}${location.search}`} replace />;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - CRITICAL for OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/seleccion" element={<SeleccionUnidad />} />
      <Route path="/contratar/:unidad" element={<ContratarUnidad />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/esperando-aprobacion" element={<EsperandoAprobacion />} />
      <Route path="/cotizar/:usuarioId" element={<CotizarPublico />} />
      <Route path="/activar-cuenta/:token" element={<ActivarCuenta />} />
      <Route path="/reset-password/:token" element={<ActivarCuenta />} />
      {/* Rutas legacy del cotizador viejo — redirigimos al nuevo cotizador (vitrina). Iter93k */}
      <Route path="/cotizador/:usuarioId" element={<RedirectCotizador />} />
      <Route path="/cotizador" element={<RedirectCotizador />} />
      <Route path="/cotizador-publico/:usuarioId" element={<RedirectCotizador />} />
      <Route path="/cotizador-publico" element={<RedirectCotizador />} />
      <Route path="/cotizar-evento/:usuarioId" element={<RedirectCotizador />} />
      <Route path="/cotizar-evento" element={<RedirectCotizador />} />
      <Route path="/reservar" element={<ReservarPetit />} />
      <Route path="/reservar/:unidad" element={<ReservarPetit />} />
      <Route path="/reservar-petit" element={<ReservarPetit />} />
      <Route path="/celebra-petit" element={<ReservarPetit />} />
      <Route path="/la-kermesita" element={<ReservarPetit />} />
      <Route path="/bocatto" element={<ReservarPetit />} />
      <Route path="/cotizacion/:token" element={<CotizacionPublica />} />
      <Route path="/formulario" element={<ClienteFormulario />} />
      <Route path="/catalogo" element={<CatalogoPublico />} />
      <Route path="/firma/:contratoId/:token" element={<FirmaContrato />} />
      <Route path="/panel" element={<ProtectedRoute><PanelWithWorkspace /></ProtectedRoute>}>
        <Route index element={<Navigate to="mi-panel" replace />} />
        <Route path="inicio" element={<Navigate to="/panel/mi-panel" replace />} />
        <Route path="hoy" element={<Navigate to="/panel/mi-panel" replace />} />
        <Route path="dashboard" element={<Navigate to="/panel/mi-panel" replace />} />
        <Route path="mi-panel" element={<Dashboard />} />
        <Route path="dashboard-celebra" element={<DashboardConsolidado />} />
        <Route path="solicitudes" element={<Solicitudes />} />
        <Route path="clientes" element={<Clientes />} />
        <Route path="clientes/:clienteId" element={<ClienteDetalle />} />
        <Route path="pagos" element={<Pagos />} />
        <Route path="invitaciones" element={<Invitaciones />} />
        <Route path="invitaciones/:invitacionId/disenar" element={<InvitacionDesigner />} />
        <Route path="whatsapp" element={<CRMWhatsApp />} />
        <Route path="calendario" element={<Calendario />} />
        <Route path="cotizaciones" element={<Cotizaciones />} />
        <Route path="cotizador-config" element={<CotizadorConfig />} />
        <Route path="contratos" element={<Contratos />} />
        <Route path="personal" element={<Personal />} />
        <Route path="proveedores" element={<Proveedores />} />
        <Route path="eventos" element={<Eventos />} />
        <Route path="eventos/:eventoId" element={<ExpedienteEvento />} />
        <Route path="eventos/:eventoId/documentos" element={<DocumentosEvento />} />
        <Route path="eventos/:eventoId/hoja-operativa" element={<HojaOperativa />} />
        <Route path="seguimientos" element={<Seguimientos />} />
        <Route path="catalogo" element={<Catalogo />} />
        <Route path="servicios-rapidos" element={<ServiciosRapidos />} />
        <Route path="finanzas" element={<Finanzas />} />
        <Route path="inventario" element={<Inventario />} />
        <Route path="compras" element={<Compras />} />
        <Route path="planner-clasico" element={<PlannerHoy />} />
        <Route path="nuevo-evento" element={<WizardNuevoEvento />} />
        <Route path="unidades-publicas" element={<UnidadesPublicas />} />
        <Route path="admin/roles" element={<RolesInternos />} />
        <Route path="reportes-unidades" element={<ReportesUnidades />} />
        <Route path="recetas" element={<Recetas />} />
        <Route path="menus" element={<MenusCRUD />} />
        <Route path="experiencias" element={<ExperienciasCRUD />} />
        {/* Iter124m · Ruta interna legacy retirada — el acceso al cotizador público
            del tenant se hace vía /panel/cotizador-config (canónico). El archivo
            admin/CotizadorPublico.jsx queda como LEGACY sin uso runtime. */}
        <Route path="cotizador-publico" element={<Navigate to="/panel/cotizador-config" replace />} />
        <Route path="recordatorios-whatsapp" element={<RecordatoriosWhatsApp />} />
        <Route path="whatsapp-plantillas" element={<WhatsappPlantillas />} />
        <Route path="suscripciones" element={<Suscripciones />} />
        <Route path="mi-suscripcion" element={<MiSuscripcion />} />
        <Route path="configuracion" element={<Configuracion />} />
        <Route path="auditoria" element={<Auditoria />} />
        <Route path="plantillas" element={<Plantillas />} />
        <Route path="plantillas/:id" element={<PlantillaBuilder />} />
        <Route path="cotizar" element={<CotizarRapido />} />
        <Route path="cotizar/:id" element={<CotizarRapido />} />
        <Route path="produccion" element={<Produccion />} />
        <Route path="operacion" element={<OperacionDashboard />} />
        <Route path="agenda" element={<AgendaEventos />} />
        <Route path="mi-agenda" element={<MiAgenda />} />
        <Route path="mi-agenda-personal" element={<MiAgendaPersonal />} />
        <Route path="agenda-personal" element={<MiAgendaPersonal />} />
        <Route path="modo-evento/:id" element={<ModoEvento />} />
        <Route path="branding" element={<Branding />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="finanzas-pro" element={<FinanzasNueva />} />
        <Route path="admin/empresas" element={<AdminEmpresas />} />
        <Route path="admin/unidades" element={<AdminUnidadesConfig />} />
        <Route path="admin/usuarios" element={<AdminUsuariosTenant />} />
        <Route path="admin/auditoria" element={<AdminAuditoria />} />
        <Route path="admin/sin-clasificar" element={<SinClasificar />} />
        <Route path="creador/revision" element={<PanelRevisionManual />} />
        <Route path="dashboard-unidades" element={<DashboardGeneral />} />
        <Route path="petit" element={<PanelPetit />} />
        <Route path="kermesita" element={<PanelKermesita />} />
        <Route path="bocatto" element={<PanelBocatto />} />
        <Route path="petit/dashboard" element={<PanelPetit />} />
        <Route path="kermesita/dashboard" element={<PanelKermesita />} />
        <Route path="bocatto/dashboard" element={<PanelBocatto />} />
      </Route>
      {/* Redirect old /admin routes to /panel */}
      <Route path="/admin/*" element={<Navigate to="/panel" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
